import {
  marketLocationAnnualPlanning,
  marketLocationFeedIn,
  marketLocationForecast,
  marketLocationMasterData,
  marketLocationOutages,
  optimization,
  optimizationGroups,
  root,
} from '@e2m-customer-portal/core';
import {
  AnnualPlanningTable,
  CoLocationOverviewPage,
  ControlEnergyForecast,
  FeedInPage,
  OptimizationGroupPage,
  OptimizationPage,
  Outages,
  ShortTermForecast,
} from '@e2m-customer-portal/domains';
import {
  MarketLocationProvider,
  useCurrentEnv,
  useFeatureFlags,
  useMarketLocationId,
  useMarketLocationSync,
  useTokenInfo,
} from '@e2m-customer-portal/hooks';
import {
  isBattery,
  isBiogas,
  isCoLocation,
  isControlEnergy,
  isFluctuating,
  UserRole,
} from '@e2m-customer-portal/shared';
import { ErrorComponent, Loading, LoadingError, ModuleTabs, SweptTab } from '@e2m-customer-portal/ui';
import { addDays, isAfter, isWithinInterval, parseISO } from 'date-fns';
import { ReactElement } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, Route, Routes, useLocation } from 'react-router';
import { hasDevices } from './utils';

export interface ForecastPageProps {
  sidebarOffset?: number;
}

export function ForecastPage({ sidebarOffset = 0 }: Readonly<ForecastPageProps>): ReactElement<ForecastPageProps> {
  const [t] = useTranslation();
  const { pathname } = useLocation();
  const marketLocationId = useMarketLocationId();
  const { areOptimizationGroupsEnabled, isOptimizationGroupRedesignEnabled, modernBatteries } = useFeatureFlags();
  const { roles } = useTokenInfo();
  const env = useCurrentEnv();

  if (marketLocationId == null) {
    throw new Error('Failed to retrieve marketLocation id');
  }

  const { loading, error, data } = useMarketLocationSync(marketLocationId);
  const marketLocation = data?.marketLocation;

  const hasBattery =
    isCoLocation(marketLocation?.technologies.concat(marketLocation.linkedMarketLocation?.technologies ?? [])) ||
    isBattery(marketLocation?.technologies.concat(marketLocation.linkedMarketLocation?.technologies ?? []));

  // Take the first forecast property that’s either currently active or,
  // if there isn’t one active right now, the very next one
  const currentForecastProperty = marketLocation?.forecastProperties
    ? [...marketLocation.forecastProperties]
        .sort((propA, propB) => parseISO(propA.validFrom).getTime() - parseISO(propB.validFrom).getTime())
        .find(
          (prop) =>
            isWithinInterval(Date.now(), { start: prop.validFrom, end: prop.validTo ?? addDays(new Date(), 1) }) ||
            isAfter(prop.validFrom, Date.now()),
        )
    : undefined;

  const tradingGroupId = marketLocation?.machines[0]?.devices[0]?.tradingGroupId;

  if (data && !data.marketLocation.permissions.seeForecast) {
    return <Navigate to={marketLocationMasterData.getLink(marketLocationId)} replace />;
  }

  if (loading) {
    return <Loading />;
  }

  const optimizationGroupIsVisible = roles.has(UserRole.VPPOperator) || isBiogas(marketLocation?.technologies);

  return (
    <div className="flex flex-col">
      {(!modernBatteries || roles.has(UserRole.VPPOperator) || !hasBattery) && (
        <ModuleTabs
          scrollButtons={false}
          value={pathname}
          className="w-fit origin-bottom-left scale-75 md:w-full md:transform-none"
        >
          <SweptTab
            component={Link}
            to={marketLocationFeedIn.getLink(marketLocationId)}
            value={decodeURIComponent(marketLocationFeedIn.getLink(marketLocationId))}
            label={
              marketLocation && isFluctuating(marketLocation.technologies)
                ? t('MARKET_LOCATION_FORECAST_TAB_SOLAR_MEASUREMENTS')
                : t('MARKET_LOCATION_FORECAST_TAB_FEED_IN')
            }
          />

          {/* Biogas */}
          {marketLocation &&
            !isFluctuating(marketLocation.technologies) &&
            !isControlEnergy(marketLocation.technologies) &&
            !isCoLocation(marketLocation.technologies) &&
            hasDevices(marketLocation) && (
              <SweptTab
                component={Link}
                to={marketLocationForecast.getLink(marketLocationId)}
                value={decodeURIComponent(marketLocationForecast.getLink(marketLocationId))}
                label={t('MARKET_LOCATION_FORECAST_TAB_WEEKLY_PLANNING')}
              />
            )}

          {marketLocation &&
            !isFluctuating(marketLocation.technologies) &&
            !isControlEnergy(marketLocation.technologies) &&
            !isCoLocation(marketLocation.technologies) &&
            hasDevices(marketLocation) && (
              <SweptTab
                component={Link}
                to={marketLocationAnnualPlanning.getLink(marketLocationId)}
                value={decodeURIComponent(marketLocationAnnualPlanning.getLink(marketLocationId))}
                label={t('MARKET_LOCATION_FORECAST_TAB_FORECAST_TABLE')}
              />
            )}

          {/* Control Energy */}
          {marketLocation &&
            !isFluctuating(marketLocation.technologies) &&
            (isControlEnergy(marketLocation.technologies) || isCoLocation(marketLocation.technologies)) &&
            hasDevices(marketLocation) && (
              <SweptTab
                component={Link}
                to={marketLocationForecast.getLink(marketLocationId)}
                value={decodeURIComponent(marketLocationForecast.getLink(marketLocationId))}
                label={t('MARKET_LOCATION_FORECAST_TAB_WEEKLY_PLANNING')}
              />
            )}

          {/* Fluctuating */}
          {marketLocation &&
            isFluctuating(marketLocation.technologies) &&
            !isControlEnergy(marketLocation.technologies) &&
            hasDevices(marketLocation) && (
              <SweptTab
                component={Link}
                to={marketLocationOutages.getLink(marketLocationId)}
                value={decodeURIComponent(marketLocationOutages.getLink(marketLocationId))}
                label={t('MARKET_LOCATION_FORECAST_TAB_OUTAGES')}
              />
            )}

          {/* Optimization Groups */}
          {optimizationGroupIsVisible &&
            marketLocation?.permissions.seeForecast &&
            tradingGroupId &&
            areOptimizationGroupsEnabled && (
              <SweptTab
                component={Link}
                sx={{ textWrap: 'wrap' }}
                to={optimizationGroups.getLink(marketLocationId)}
                value={decodeURIComponent(optimizationGroups.getLink(marketLocationId))}
                label={
                  env !== 'PROD' && marketLocationId === '213687'
                    ? t('HEADER_MENU_OPTIMIZATION_GROUPS_TEST')
                    : t('HEADER_MENU_OPTIMIZATION_GROUPS')
                }
              />
            )}

          {/* Optimization Group Management */}
          {isBiogas(marketLocation?.technologies) &&
            marketLocation?.permissions.seeForecast &&
            tradingGroupId &&
            isOptimizationGroupRedesignEnabled && (
              <SweptTab
                component={Link}
                to={optimization.getLink(marketLocationId)}
                value={decodeURIComponent(optimization.getLink(marketLocationId))}
                label={t('HEADER_MENU_OPTIMIZATION')}
              />
            )}
        </ModuleTabs>
      )}

      <ErrorBoundary FallbackComponent={ErrorComponent}>
        <LoadingError loading={loading} error={error}>
          {marketLocation && (
            <MarketLocationProvider value={marketLocation}>
              <Routes>
                {/* Biogas & Biomass */}
                {!isFluctuating(marketLocation.technologies) &&
                  !isControlEnergy(marketLocation.technologies) &&
                  !isCoLocation(marketLocation.technologies) &&
                  hasDevices(marketLocation) && (
                    <Route
                      path={marketLocationForecast.relativePath}
                      element={
                        <ShortTermForecast
                          marketLocation={marketLocation}
                          sidebarOffset={sidebarOffset}
                          currentForecastProperty={currentForecastProperty}
                        />
                      }
                    />
                  )}

                {/* Optimization Groups */}
                {optimizationGroupIsVisible && marketLocation?.permissions.seeForecast && tradingGroupId && (
                  <Route
                    path={optimizationGroups.relativePath}
                    element={
                      <OptimizationGroupPage
                        optimizationGroupId={
                          env !== 'PROD' && marketLocationId === '213687' ? '200010' : tradingGroupId
                        }
                        marketLocations={[marketLocation]}
                        isTestEnvironment={env !== 'PROD' && marketLocationId === '213687'}
                      />
                    }
                  />
                )}

                {/* Optimization Group Management */}
                {isBiogas(marketLocation?.technologies) && marketLocation.permissions.seeForecast && tradingGroupId && (
                  <Route
                    path={optimization.relativePath}
                    element={
                      <OptimizationPage optimizationGroupId={tradingGroupId} marketLocations={[marketLocation]} />
                    }
                  />
                )}

                {!isFluctuating(marketLocation.technologies) &&
                  !isControlEnergy(marketLocation.technologies) &&
                  !isCoLocation(marketLocation.technologies) &&
                  hasDevices(marketLocation) && (
                    <Route
                      path={marketLocationAnnualPlanning.relativePath}
                      element={<AnnualPlanningTable marketLocation={marketLocation} />}
                    />
                  )}

                {/* Control Energy */}
                {!isFluctuating(marketLocation.technologies) &&
                  (isControlEnergy(marketLocation.technologies) || isCoLocation(marketLocation.technologies)) &&
                  hasDevices(marketLocation) && (
                    <Route
                      path={marketLocationForecast.relativePath}
                      element={
                        <ControlEnergyForecast
                          marketLocation={marketLocation}
                          currentForecastProperty={currentForecastProperty}
                        />
                      }
                    />
                  )}

                {/* Fluctuating */}
                {isFluctuating(marketLocation.technologies) &&
                  !isControlEnergy(marketLocation.technologies) &&
                  hasDevices(marketLocation) && (
                    <Route
                      path={marketLocationOutages.relativePath}
                      element={<Outages marketLocation={marketLocation} />}
                    />
                  )}

                {!modernBatteries || !hasBattery ? (
                  <Route
                    path={marketLocationFeedIn.relativePath}
                    element={
                      <FeedInPage marketLocation={marketLocation} resolution={currentForecastProperty?.resolution} />
                    }
                  />
                ) : (
                  <Route
                    path={marketLocationFeedIn.relativePath}
                    element={
                      <CoLocationOverviewPage
                        marketLocation={marketLocation}
                        currentForecastProperty={currentForecastProperty}
                      />
                    }
                  />
                )}

                {
                  <Route
                    path="*"
                    element={
                      <Navigate
                        to={marketLocationId ? marketLocationFeedIn.getLink(marketLocationId) : root.getLink()}
                        replace
                      />
                    }
                  />
                }
              </Routes>
            </MarketLocationProvider>
          )}
        </LoadingError>
      </ErrorBoundary>
    </div>
  );
}
